import { useEffect, useRef, useState } from "react";

// SUR-1868 — debounced ez location type-ahead for the Connect Custom Search
// island. Fetches /_locations as the visitor types and returns the parsed
// { id, text, category } suggestions. Degrade-safe: a failed/empty fetch yields
// []. Stale responses are dropped (a request-id guard), so fast typing can't let
// an older keystroke's results clobber a newer one's. `within` (the chosen
// primary locations) bounds the secondary picker's suggestions.
export const DEBOUNCE_MS = 220;
export const MIN_CHARS = 2;

export function useLocationSuggestions({ endpoint = "/_locations", text, locationType, within }) {
  const [suggestions, setSuggestions] = useState([]);
  // True from the keystroke that crosses MIN_CHARS until its results (or an
  // error) land — lets the field show a "Searching…" hint right away.
  const [loading, setLoading] = useState(false);
  const reqIdRef = useRef(0);
  // `within` is an array — depend on a stable string so a fresh [] identity each
  // render doesn't re-fire the effect.
  const withinKey = (within || []).join("|");

  useEffect(() => {
    const term = (text || "").trim();
    if (term.length < MIN_CHARS) {
      setSuggestions([]);
      setLoading(false);
      return undefined;
    }
    const id = (reqIdRef.current += 1);
    setLoading(true);
    const handle = setTimeout(async () => {
      const params = new URLSearchParams({ q: term });
      if (locationType) params.set("location_type", locationType);
      (within || []).forEach((w) => params.append("within[]", w));
      try {
        const res = await fetch(`${endpoint}?${params.toString()}`, { credentials: "same-origin" });
        const data = await res.json();
        if (id !== reqIdRef.current) return; // a newer keystroke superseded this
        setSuggestions(Array.isArray(data?.results) ? data.results : []);
        setLoading(false);
      } catch {
        if (id === reqIdRef.current) { setSuggestions([]); setLoading(false); }
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, endpoint, locationType, withinKey]);

  // Drop any in-flight result and empty the list (used after a pick/submit).
  const clear = () => {
    reqIdRef.current += 1;
    setSuggestions([]);
    setLoading(false);
  };

  return { suggestions, loading, clear };
}
